import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Phone,
  MapPin,
  Wrench,
  CheckCircle2,
} from "lucide-react";
import Header from "./site/Header";
import Footer from "./site/Footer";
import Contact from "./site/Contact";
import { SITE } from "../site.config";
import { BUILDS, findBuild, BUILD_PATH } from "../data/builds";
import { SERVICES } from "../data/services";
import { INDEXABLE_SUBURBS, SUBURB_PATH } from "../suburbs";
import useDocumentMeta from "../hooks/useDocumentMeta";

const SITE_URL = "https://powerzautoelectrical.com.au";

export default function BuildPage() {
  const { slug } = useParams();
  const build = findBuild(slug);
  const safe = build || {};
  const canonical = `${SITE_URL}/builds/${slug || ""}`;

  const articleJsonLd = build && {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: safe.title,
    description: safe.metaDesc,
    image: (safe.gallery || []).map((g) => `${SITE_URL}${g.src}`),
    datePublished: safe.date,
    dateModified: safe.date,
    author: {
      "@type": "Organization",
      name: SITE.business.fullName,
      url: SITE_URL,
    },
    publisher: {
      "@type": "Organization",
      name: SITE.business.fullName,
      logo: { "@type": "ImageObject", url: SITE.business.logo },
    },
    mainEntityOfPage: canonical,
  };

  const breadcrumbJsonLd = build && {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      {
        "@type": "ListItem",
        position: 2,
        name: "Completed Builds",
        item: `${SITE_URL}/builds`,
      },
      { "@type": "ListItem", position: 3, name: safe.title, item: canonical },
    ],
  };

  useDocumentMeta({
    title: safe.metaTitle || "Build | Powerz Auto Electrical",
    description: safe.metaDesc || "",
    canonical,
    image: safe.hero ? `${SITE_URL}${safe.hero}` : SITE.business.logo,
    jsonLd: build ? [articleJsonLd, breadcrumbJsonLd].filter(Boolean) : [],
  });

  if (!build) return <Navigate to="/builds" replace />;

  const relatedServiceObjects = (build.relatedServices || [])
    .map((s) => SERVICES.find((x) => x.slug === s))
    .filter(Boolean);

  // Topical-cluster suggestions: every dual battery build should also link to
  // anderson plugs, towing electrics, caravan repairs etc. Add a curated list
  // of cross-related services so Google sees a rich internal-link cluster.
  const CROSS_LINK_MAP = {
    "dual-battery-systems": ["12-pin-trailer-plug-install", "redarc-brake-controllers", "lithium-battery-installs", "4wd-electrical-fitouts"],
    "lithium-battery-installs": ["dual-battery-systems", "4wd-electrical-fitouts", "starlink-vehicle-installs", "canopy-electrical-fitouts"],
    "redarc-brake-controllers": ["12-pin-trailer-plug-install", "dual-battery-systems", "caravan-electrical-repairs", "4wd-electrical-fitouts"],
    "12-pin-trailer-plug-install": ["redarc-brake-controllers", "caravan-electrical-repairs", "dual-battery-systems"],
    "caravan-electrical-repairs": ["12-pin-trailer-plug-install", "redarc-brake-controllers", "dual-battery-systems", "auto-air-conditioning"],
    "auto-air-conditioning": ["caravan-electrical-repairs", "4wd-electrical-fitouts"],
    "4wd-electrical-fitouts": ["dual-battery-systems", "lithium-battery-installs", "12-pin-trailer-plug-install", "starlink-vehicle-installs", "canopy-electrical-fitouts"],
    "canopy-electrical-fitouts": ["dual-battery-systems", "lithium-battery-installs", "4wd-electrical-fitouts"],
    "starlink-vehicle-installs": ["lithium-battery-installs", "4wd-electrical-fitouts", "canopy-electrical-fitouts"],
  };

  const crossLinkedSlugs = new Set();
  (build.relatedServices || []).forEach((primary) => {
    (CROSS_LINK_MAP[primary] || []).forEach((slug) => crossLinkedSlugs.add(slug));
  });
  // Remove anything already in primary relatedServices to avoid duplicates
  (build.relatedServices || []).forEach((slug) => crossLinkedSlugs.delete(slug));
  const crossLinkedServiceObjects = Array.from(crossLinkedSlugs)
    .map((slug) => SERVICES.find((x) => x.slug === slug))
    .filter(Boolean)
    .slice(0, 4);

  // Same-category builds (other builds that share at least one related service tag)
  // — this gives the "More Real Jobs Like This" topical cluster Google rewards.
  const sameCategoryBuilds = BUILDS.filter(
    (b) =>
      b.slug !== build.slug &&
      Array.isArray(b.relatedServices) &&
      Array.isArray(build.relatedServices) &&
      b.relatedServices.some((s) => build.relatedServices.includes(s))
  ).slice(0, 3);

  // Fallback to most-recent other builds if no category matches
  const otherBuilds =
    sameCategoryBuilds.length > 0
      ? sameCategoryBuilds
      : BUILDS.filter((b) => b.slug !== build.slug).slice(0, 3);

  // Suburb cluster — only link if the customer's suburb is an indexable page,
  // otherwise mention as text-only in the copy.
  const customerSuburb = INDEXABLE_SUBURBS.find(
    (s) =>
      build.suburb &&
      s.name.toLowerCase() === build.suburb.toLowerCase()
  );
  const nearbySuburbs = INDEXABLE_SUBURBS
    .filter((s) => !customerSuburb || s.slug !== customerSuburb.slug)
    .slice(0, 5);

  return (
    <div className="bg-black text-white">
      <Header />

      {/* HERO */}
      <section
        data-testid={`build-hero-${build.slug}`}
        className="relative min-h-[70svh] w-full overflow-hidden bg-black pt-40 md:pt-48"
      >
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: `url(${build.hero})` }}
          aria-hidden="true"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.7) 60%, #000 100%)",
          }}
          aria-hidden="true"
        />
        <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 pb-20">
          <Link
            to="/builds"
            data-testid="build-back-link"
            className="inline-flex items-center gap-2 text-white/60 hover:text-cyan-pz transition-colors bebas tracking-widest text-sm mb-10"
          >
            <ArrowLeft className="w-4 h-4" />
            ALL BUILDS
          </Link>

          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-cyan-pz" />
            <span className="bebas text-cyan-pz tracking-[0.3em] text-sm md:text-base">
              COMPLETED JOB • {build.suburb.toUpperCase()}
            </span>
          </div>

          <h1
            data-testid="build-headline"
            className="bebas text-white text-4xl md:text-6xl lg:text-7xl leading-[0.9] tracking-tight max-w-5xl"
          >
            <span className="text-cyan-pz glow-cyan">{build.title.toUpperCase()}</span>
          </h1>

          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-white/70 barlow text-sm">
            <span className="inline-flex items-center gap-2">
              <Wrench className="w-4 h-4 text-cyan-pz" />
              {build.vehicle}
            </span>
            <span className="inline-flex items-center gap-2">
              <MapPin className="w-4 h-4 text-cyan-pz" />
              {build.suburb}
            </span>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <a
              href={`tel:${SITE.business.phoneTel}`}
              data-testid="build-call-btn"
              className="inline-flex items-center justify-center gap-3 bg-cyan-pz text-black px-8 py-5 bebas text-xl tracking-[0.2em] hover:bg-white transition-colors clip-perf pulse-cyan"
            >
              <Phone className="w-5 h-5" />
              BOOK A SIMILAR JOB
            </a>
            <a
              href={`mailto:${SITE.business.email}?subject=Powerz%20Auto%20Electrical%20-%20${encodeURIComponent(build.title)}%20Enquiry`}
              data-testid="build-quote-btn"
              className="inline-flex items-center justify-center gap-3 border border-white/30 text-white px-8 py-5 bebas text-xl tracking-[0.2em] hover:border-cyan-pz hover:text-cyan-pz transition-colors"
            >
              EMAIL US
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* SUMMARY + WRITEUP */}
      <section className="relative bg-black py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 space-y-6">
          <p className="text-cyan-pz/80 barlow text-xl md:text-2xl leading-relaxed border-l-2 border-cyan-pz pl-6">
            {build.summary}
          </p>
          {build.writeup.map((p, i) => (
            <p
              key={i}
              data-testid={`build-writeup-${i}`}
              className="text-white/80 barlow text-lg leading-relaxed"
            >
              {p}
            </p>
          ))}
        </div>
      </section>

      {/* GALLERY */}
      <section
        data-testid="build-gallery"
        className="relative bg-[#0A0A0A] py-20 md:py-28 border-y border-white/10"
      >
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-10 bg-cyan-pz" />
            <span className="bebas text-cyan-pz tracking-[0.3em] text-sm">
              THE JOB IN PHOTOS
            </span>
          </div>
          <h2 className="bebas text-white text-3xl md:text-5xl leading-[0.95] mb-12">
            INSTALLED. <span className="text-cyan-pz">CLEAN.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {build.gallery.map((g, i) => (
              <figure
                key={i}
                data-testid={`build-gallery-${i}`}
                className="bg-black border border-white/10 overflow-hidden clip-perf"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={g.src}
                    alt={g.alt}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                </div>
                {g.caption && (
                  <figcaption className="px-5 py-4 text-white/60 barlow text-sm leading-relaxed">
                    {g.caption}
                  </figcaption>
                )}
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* GEAR LIST */}
      {build.gear && build.gear.length > 0 && (
        <section
          data-testid="build-gear"
          className="relative bg-black py-20"
        >
          <div className="max-w-5xl mx-auto px-5 lg:px-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-cyan-pz" />
              <span className="bebas text-cyan-pz tracking-[0.3em] text-sm">
                GEAR LIST
              </span>
            </div>
            <h2 className="bebas text-white text-3xl md:text-5xl leading-[0.95] mb-10">
              WHAT WENT IN.
            </h2>
            <ul className="divide-y divide-white/10 border border-white/10 bg-[#0A0A0A] clip-perf">
              {build.gear.map((g, i) => (
                <li
                  key={i}
                  className="flex items-start gap-4 px-6 py-4"
                >
                  <CheckCircle2 className="w-5 h-5 text-cyan-pz mt-0.5 shrink-0" />
                  <div>
                    <p className="bebas text-white tracking-wider text-lg">
                      {g.name.toUpperCase()}
                    </p>
                    <p className="text-white/50 barlow text-sm">{g.role}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* RELATED SERVICE — primary services Powerz offers that match this build */}
      {relatedServiceObjects.length > 0 && (
        <section
          data-testid="build-related-services"
          className="relative bg-[#0A0A0A] py-16 border-y border-white/10"
        >
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-cyan-pz" />
              <span className="bebas text-cyan-pz tracking-[0.3em] text-sm">
                BOOK THE SAME WORK
              </span>
            </div>
            <h2 className="bebas text-white text-3xl md:text-5xl leading-[0.9] mb-8">
              WANT THIS ON YOUR RIG?
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {relatedServiceObjects.map((s) => (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  data-testid={`build-related-service-${s.slug}`}
                  className="group flex items-center justify-between gap-4 border border-cyan-pz/30 bg-black p-6 hover:border-cyan-pz hover:bg-cyan-pz/5 transition-colors clip-perf"
                >
                  <div>
                    <p className="bebas text-cyan-pz tracking-[0.2em] text-xs mb-1">
                      VIEW SERVICE
                    </p>
                    <h3 className="bebas text-white text-2xl tracking-wider">
                      {s.name.toUpperCase()}
                    </h3>
                  </div>
                  <ArrowRight className="w-6 h-6 text-cyan-pz group-hover:translate-x-1 transition-transform" />
                </Link>
              ))}
            </div>

            {/* CROSS-LINKED SERVICES — topical-cluster signals to Google */}
            {crossLinkedServiceObjects.length > 0 && (
              <div className="mt-10" data-testid="build-cross-linked-services">
                <p className="bebas text-cyan-pz/80 tracking-[0.25em] text-xs mb-4">
                  PEOPLE WHO BOOK THIS ALSO BOOK
                </p>
                <div className="flex flex-wrap gap-2">
                  {crossLinkedServiceObjects.map((s) => (
                    <Link
                      key={s.slug}
                      to={`/services/${s.slug}`}
                      data-testid={`build-cross-service-${s.slug}`}
                      className="bebas text-sm tracking-[0.18em] border border-white/15 bg-black px-4 py-2 text-white/80 hover:text-cyan-pz hover:border-cyan-pz transition-colors"
                    >
                      {s.name.toUpperCase()}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* SERVICE AREA — link to the customer's suburb page + nearby suburbs */}
      <section
        data-testid="build-service-area"
        className="relative bg-black py-12 border-b border-white/10"
      >
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-10 bg-cyan-pz" />
            <span className="bebas text-cyan-pz tracking-[0.3em] text-sm">
              WE SERVICE
            </span>
          </div>
          <h2 className="bebas text-white text-2xl md:text-4xl leading-[0.95] mb-6">
            {customerSuburb ? (
              <>
                AUTO ELECTRICIAN <span className="text-cyan-pz">{customerSuburb.name.toUpperCase()}</span> &amp; ACROSS THE SUNSHINE COAST.
              </>
            ) : (
              <>
                ACROSS THE <span className="text-cyan-pz">SUNSHINE COAST.</span>
              </>
            )}
          </h2>
          <p className="text-white/60 barlow text-sm md:text-base max-w-3xl mb-6">
            Workshop at Kunda Park{customerSuburb ? `, customer for this build from ${customerSuburb.name}` : ""}. Customers drive in from every Sunshine Coast suburb — quick links to your local page:
          </p>
          <div className="flex flex-wrap gap-2">
            {customerSuburb && (
              <Link
                to={SUBURB_PATH(customerSuburb.slug)}
                data-testid={`build-suburb-primary-${customerSuburb.slug}`}
                className="bebas text-sm tracking-[0.18em] border border-cyan-pz bg-cyan-pz/10 text-cyan-pz px-4 py-2 hover:bg-cyan-pz hover:text-black transition-colors inline-flex items-center gap-2"
              >
                <MapPin className="w-3.5 h-3.5" />
                AUTO ELECTRICIAN {customerSuburb.name.toUpperCase()}
              </Link>
            )}
            {nearbySuburbs.map((s) => (
              <Link
                key={s.slug}
                to={SUBURB_PATH(s.slug)}
                data-testid={`build-suburb-${s.slug}`}
                className="bebas text-sm tracking-[0.18em] border border-white/15 bg-black px-4 py-2 text-white/80 hover:text-cyan-pz hover:border-cyan-pz transition-colors inline-flex items-center gap-2"
              >
                <MapPin className="w-3.5 h-3.5 text-cyan-pz/60" />
                AUTO ELECTRICIAN {s.name.toUpperCase()}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* OTHER BUILDS — same-category builds for topical cluster */}
      {otherBuilds.length > 0 && (
        <section className="relative bg-black py-16">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-cyan-pz" />
              <span className="bebas text-cyan-pz tracking-[0.3em] text-sm">
                MORE LIKE THIS
              </span>
            </div>
            <h2 className="bebas text-white text-3xl md:text-5xl leading-[0.9] mb-10">
              MORE REAL JOBS <br />
              <span className="text-cyan-pz">WE'VE FINISHED.</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {otherBuilds.map((b) => (
                <Link
                  key={b.slug}
                  to={BUILD_PATH(b.slug)}
                  data-testid={`related-build-${b.slug}`}
                  className="group block bg-[#0A0A0A] border border-white/10 hover:border-cyan-pz transition-colors overflow-hidden clip-perf"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={b.hero}
                      alt={b.title}
                      className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-5">
                    <p className="bebas text-cyan-pz/70 tracking-[0.2em] text-xs mb-1">
                      {b.suburb ? b.suburb.toUpperCase() : "SUNSHINE COAST"}
                    </p>
                    <h3 className="bebas text-white text-xl tracking-wider group-hover:text-cyan-pz transition-colors">
                      {b.title.toUpperCase()}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
            <div className="mt-8">
              <Link
                to="/builds"
                data-testid="build-all-builds-link"
                className="inline-flex items-center gap-3 border border-cyan-pz/40 text-cyan-pz px-6 py-3 bebas text-base tracking-[0.2em] hover:bg-cyan-pz hover:text-black transition-colors"
              >
                SEE ALL COMPLETED BUILDS
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      <Contact />
      <Footer />
    </div>
  );
}
